import { useState } from "react";
import Button from "../components/Button";
import styles from "./AuthRecovery.module.css";
import type { SessionState, TokenBroker } from "./token-broker";

type SignInRequired = Extract<SessionState, { status: "signin-required" }>;

type AuthRecoveryProps = {
  broker: TokenBroker;
  session: SignInRequired;
  /** Full page when no account exists; otherwise a banner that keeps the app usable. */
  variant: "page" | "banner";
};

const AuthRecovery = ({ broker, session, variant }: AuthRecoveryProps) => {
  const [busy, setBusy] = useState(false);

  const signInAgain = async () => {
    setBusy(true);
    try {
      await broker.recover();
    } finally {
      setBusy(false);
    }
  };

  const action = (
    <Button
      variant="primary"
      type="button"
      text="Sign in again"
      disabled={busy}
      onClick={() => void signInAgain()}
    />
  );

  if (variant === "banner") {
    return (
      <div className={styles.banner} role="alert">
        <p>Your session needs to be renewed.</p>
        {action}
      </div>
    );
  }

  return (
    <main className={styles.screen}>
      <section className={styles.content} aria-labelledby="auth-error-title">
        <div role="alert">
          <h1 id="auth-error-title">Session needs attention</h1>
          <p>We couldn't complete sign-in. Please try again.</p>
        </div>
        <div className={styles.actions}>{action}</div>
        {busy && <p role="status">Signing in...</p>}
        {(session.correlationId ?? session.errorCode) && (
          <p className={styles.reference}>
            Reference: {session.correlationId ?? session.errorCode}
          </p>
        )}
      </section>
    </main>
  );
};

export default AuthRecovery;
